import clsx from "clsx";
import { useRouter } from "next/router";
import Link from "next/link";

import { BRAND_LOGGED_IN_START_PAGE, BRAND_PATHS } from "@chef/constants";
import { getNextCutoff } from "@chef/helpers";
import { isEqualStrings } from "@chef/utils/equal";
import { Breakfast, Plate, Cart } from "@chef/icons/large";
import { DeviationIndicator } from "@chef/smart-components";
import { useViewport } from "@chef/hooks";
import { useExtendedBasketData } from "@chef/state-management/hooks";

import { Logo } from "../Logo";
import { DrawerButton } from "./Drawer/DrawerButton";
import { useSticky } from "../Sticky";
import { WeeksNavigation } from "./WeeksNavigation";

import { intl } from "./MyMenuVariant.Intl";

interface LinkWithIconProps {
  isActive: boolean;
  href: string;
  label: string;
  children: React.ReactNode;
  id: string;
  className?: string;
}

type DesktopBasketLinkWithIconProps = Omit<LinkWithIconProps, "isActive"> & {
  basketHasDeviations: boolean;
};

const LinkWithIcon = ({
  isActive,
  href,
  label,
  children,
  id,
  className,
}: LinkWithIconProps) => (
  <Link
    className={clsx(
      "relative flex items-center gap-2 lg:gap-4 group",
      className,
    )}
    href={href}
    id={id}
  >
    {children}
    <span
      className={clsx(
        "group-hover:underline decoration-2 underline-offset-8 decoration-primary",
        isActive && "underline",
      )}
    >
      {label}
    </span>
  </Link>
);

const DesktopBasketLinkWithIcon = ({
  href,
  label,
  children,
  id,
  basketHasDeviations,
}: DesktopBasketLinkWithIconProps) => {
  return (
    <Link
      className={clsx(
        "relative flex items-center gap-3 lg:gap-4 group px-6 py-2 border-2 border-solid rounded-full",
        !basketHasDeviations && "border-black hover:bg-grey-3",
        basketHasDeviations && "bg-primary border-primary",
      )}
      href={href}
      id={id}
    >
      {children}
      <span className={clsx(basketHasDeviations && "text-white")}>{label}</span>
    </Link>
  );
};

export const MyMenuVariant = () => {
  const router = useRouter();

  const { isFloating } = useSticky();

  const [breakpoint] = useViewport();
  const isDesktop = breakpoint !== "md" && breakpoint !== "sm";

  const nextCutoff = getNextCutoff();

  const week = +(router.query["week"] ?? nextCutoff.week);
  const year = +(router.query["year"] ?? nextCutoff.year);

  const isFloatingAndDesktop = isFloating && isDesktop;
  const isNotFloatingAndDesktop = !isFloating && isDesktop;

  const {
    data: { basketHasDeviations, isUndeliverable },
  } = useExtendedBasketData({ week, year });

  const indicateBasketChange = basketHasDeviations && !isUndeliverable;

  return (
    <div className="flex-col gap-2">
      <div
        id="my-menu-header"
        className="relative flex items-center justify-between w-full h-15"
      >
        <div className="flex w-24 md:w-48">
          {isFloatingAndDesktop ? (
            <nav className="flex items-center gap-7 shrink-0">
              <LinkWithIcon
                href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
                label={intl.MEALS}
                id="header-nav-dishes"
                isActive={[
                  BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }),
                  BRAND_PATHS.MY_MENU_PAGE,
                ].some((link) => isEqualStrings(router.asPath, link))}
              >
                <Plate className="w-6 h-6" />
              </LinkWithIcon>

              <LinkWithIcon
                href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
                label={intl.ADDONS}
                id="header-nav-addons"
                isActive={isEqualStrings(
                  router.asPath,
                  BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
                )}
              >
                <Breakfast className="w-6 h-6" />
              </LinkWithIcon>
            </nav>
          ) : (
            <Logo responsive href={BRAND_LOGGED_IN_START_PAGE} />
          )}
        </div>

        <WeeksNavigation />
        <div className="flex items-center justify-end w-24 gap-4 md:w-48">
          {isFloatingAndDesktop && (
            <DesktopBasketLinkWithIcon
              href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({
                week,
                year,
              })}
              label={intl.ORDER}
              id="header_nav_checkout"
              basketHasDeviations={indicateBasketChange}
            >
              <DeviationIndicator
                week={week}
                year={year}
                className="absolute left-10 top-0.5"
                variant="header"
              />
              <Cart
                className={clsx(
                  "w-6 h-6",
                  indicateBasketChange && "text-white",
                )}
              />
            </DesktopBasketLinkWithIcon>
          )}

          <DrawerButton />
        </div>
      </div>

      {isNotFloatingAndDesktop && (
        <div className="flex items-center justify-between pb-4 h-15">
          <div className="flex gap-7">
            <LinkWithIcon
              href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
              label={intl.MEALS}
              id="header-nav-dishes"
              isActive={[
                BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }),
                BRAND_PATHS.MY_MENU_PAGE,
              ].some((link) => isEqualStrings(router.asPath, link))}
            >
              <Plate className="w-6 h-6" />
            </LinkWithIcon>

            <LinkWithIcon
              href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
              label={intl.ADDONS}
              id="header-nav-addons"
              isActive={isEqualStrings(
                router.asPath,
                BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
              )}
            >
              <Breakfast className="w-6 h-6" />
            </LinkWithIcon>
          </div>
          <DesktopBasketLinkWithIcon
            href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year })}
            label={indicateBasketChange ? intl.CONFIRM_ORDER : intl.ORDER}
            id="header_nav_checkout"
            basketHasDeviations={indicateBasketChange}
          >
            <DeviationIndicator
              week={week}
              year={year}
              className="absolute left-10 top-0.5"
              variant="header"
            />
            <Cart
              className={clsx("w-6 h-6", indicateBasketChange && "text-white")}
            />
          </DesktopBasketLinkWithIcon>
        </div>
      )}
    </div>
  );
};
